/**
 * Backend Subscription Service
 * Handles communication with Laravel backend for subscription validation
 */

import api from './api';
import { getAxiosErrorMessage } from '../utils/error';
import type { Subscription } from '../types';

// ============================================
// Internal Types
// ============================================

interface BackendPlan {
  plan_id: string;
  name: string;
  price: number | string;
  [key: string]: unknown;
}

interface BackendSubscription {
  productId: string;
  purchaseToken: string;
  [key: string]: unknown;
}

interface StripeCheckoutData {
  url: string;
  [key: string]: unknown;
}

interface StripeSessionData {
  status: string;
  payment_status: string;
  amount_total?: number; // cents
  currency?: string;
  [key: string]: unknown;
}

interface PurchaseData {
  platform: 'ios' | 'android';
  product_id: string;
  /** Required for Android purchases; absent on iOS. */
  purchase_token?: string;
  /** Required for iOS purchases; absent on Android. */
  receipt_data?: string;
}

interface PlansResponse {
  success: boolean;
  message?: string;
  data: BackendPlan[];
}

interface CheckoutResponse {
  success: boolean;
  message?: string;
  data?: StripeCheckoutData;
}

interface SessionResponse {
  success: boolean;
  message?: string;
  data?: StripeSessionData;
}

interface ValidationResponse {
  success: boolean;
  message?: string;
  data?: Subscription;
  subscription?: Subscription;
}

interface CancelResponse {
  success: boolean;
  message?: string;
  error?: string | null;
}

interface PortalResponse {
  success: boolean;
  message?: string;
  data?: StripeCheckoutData;
}

interface MobileValidationResponse {
  success: boolean;
  message?: string;
  data?: Subscription;
}

// ============================================
// Helper Functions
// ============================================

/**
 * Validate that plans have required fields
 */
const validatePlansShape = (plans: unknown): plans is BackendPlan[] => {
  if (!Array.isArray(plans)) return false;
  return plans.every(
    (plan: unknown) =>
      plan &&
      typeof plan === 'object' &&
      'plan_id' in plan &&
      typeof plan.plan_id === 'string' &&
      'name' in plan &&
      typeof plan.name === 'string' &&
      'price' in plan &&
      (typeof plan.price === 'number' || typeof plan.price === 'string'),
  );
};

/**
 * Validate Stripe session response shape
 */
const validateSessionShape = (data: unknown): data is StripeSessionData => {
  return (
    data !== null &&
    typeof data === 'object' &&
    'status' in data &&
    typeof data.status === 'string' &&
    'payment_status' in data &&
    typeof data.payment_status === 'string'
  );
};

// Use shared error utility
const handleError = getAxiosErrorMessage;

// ============================================
// Public API Functions
// ============================================

/**
 * Fetch available subscription plans from backend
 * @param platform - 'web', 'ios', or 'android'
 */
export const fetchPlansFromBackend = async (
  platform: 'web' | 'ios' | 'android' = 'web',
): Promise<PlansResponse> => {
  try {
    const response = await api.get(`/subscriptions/plans?platform=${platform}`);
    const data = response.data as PlansResponse;

    if (data.success && validatePlansShape(data.data)) {
      return { success: true, data: data.data };
    }

    if (data.success && !validatePlansShape(data.data)) {
      return {
        success: false,
        message: 'Invalid response format from server',
        data: [],
      };
    }

    return {
      success: false,
      message: data.message || 'Failed to fetch plans',
      data: [],
    };
  } catch (error) {
    // 401 is handled by interceptor (auto-logout)
    return {
      success: false,
      message: handleError(error, 'Network error. Please try again.'),
      data: [],
    };
  }
};

/**
 * Create a Stripe checkout session
 * Returns a URL to redirect the user to for payment
 */
export const createStripeCheckoutSession = async (
  priceId: string,
  successUrl: string,
  cancelUrl: string,
): Promise<CheckoutResponse> => {
  try {
    const response = await api.post('/subscriptions/stripe/checkout', {
      price_id: priceId,
      success_url: successUrl,
      cancel_url: cancelUrl,
    });
    const data = response.data as CheckoutResponse;

    if (data.success && data.data?.url) {
      return { success: true, data: data.data };
    }

    if (data.success && !data.data?.url) {
      return {
        success: false,
        message: 'Invalid checkout response from server',
      };
    }

    return {
      success: false,
      message: data.message || 'Failed to create checkout session',
    };
  } catch (error) {
    return {
      success: false,
      message: handleError(error, 'Network error. Please try again.'),
    };
  }
};

/**
 * Get Stripe checkout session status
 */
export const getStripeSessionStatus = async (sessionId: string): Promise<SessionResponse> => {
  try {
    const response = await api.get(`/subscriptions/stripe/session/${sessionId}`);
    const data = response.data as SessionResponse;

    if (data.success && validateSessionShape(data.data)) {
      return { success: true, data: data.data };
    }

    if (data.success && !validateSessionShape(data.data)) {
      return {
        success: false,
        message: 'Invalid session response from server',
      };
    }

    return {
      success: false,
      message: data.message || 'Failed to get session status',
    };
  } catch (error) {
    return {
      success: false,
      message: handleError(error, 'Network error. Please try again.'),
    };
  }
};

/**
 * Validate subscription with backend
 * Sends purchase token to backend for validation with Google Play
 */
export const validateSubscriptionWithBackend = async (
  activeSubscription: BackendSubscription | null,
): Promise<ValidationResponse> => {
  if (!activeSubscription) {
    return { success: false, message: 'No subscription data' };
  }

  try {
    const response = await api.post('/subscriptions/validate', {
      platform: 'android',
      product_id: activeSubscription.productId,
      purchase_token: activeSubscription.purchaseToken,
    });
    const data = response.data as ValidationResponse;

    if (data.success) {
      return { success: true, data: data.subscription };
    }

    return { success: false, message: data.message || 'Validation failed' };
  } catch (error) {
    return {
      success: false,
      message: handleError(error, 'Network error. Please try again.'),
    };
  }
};

/**
 * Fetch subscription status from backend
 * Useful for checking subscription on app start
 */
export const fetchSubscriptionStatusFromBackend = async (): Promise<Subscription | null> => {
  try {
    const response = await api.get('/subscriptions/status');
    const data = response.data as {
      has_subscription?: boolean;
      subscription?: Subscription;
    };

    if (data.has_subscription && data.subscription) {
      return data.subscription;
    }

    return null;
  } catch (_error) {
    return null;
  }
};

/**
 * Cancel subscription (marks as cancelled in backend)
 */
export const cancelSubscriptionInBackend = async (): Promise<CancelResponse> => {
  try {
    const response = await api.post('/subscriptions/cancel');
    const data = response.data as { message?: string };

    return {
      success: true,
      message: data.message || 'Subscription cancelled',
      error: null,
    };
  } catch (error) {
    const message = handleError(error, 'Network error. Please try again.');
    return { success: false, message, error: message };
  }
};

// Alias for convenience
export const cancelSubscription = cancelSubscriptionInBackend;

/**
 * Create a Stripe Billing Portal session
 * Returns a URL to redirect the user to manage their payment method
 */
export const createStripeBillingPortalSession = async (
  returnUrl: string,
): Promise<PortalResponse> => {
  try {
    const response = await api.post('/subscriptions/stripe/portal', {
      return_url: returnUrl,
    });
    const data = response.data as PortalResponse;

    if (data.success && data.data?.url) {
      return { success: true, data: data.data };
    }

    return {
      success: false,
      message: data.message || 'Failed to create billing portal session',
    };
  } catch (error) {
    return {
      success: false,
      message: handleError(error, 'Network error. Please try again.'),
    };
  }
};

/**
 * Validate a mobile (iOS/Android) purchase with the backend
 * This should be called after the IAP plugin verifies the receipt locally
 *
 * @param purchaseData - Purchase data from the IAP plugin
 * @param purchaseData.platform - 'ios' or 'android'
 * @param purchaseData.product_id - The product ID
 * @param purchaseData.purchase_token - Purchase token (Android) or transaction ID (iOS)
 * @param purchaseData.receipt_data - Base64 receipt data (iOS only)
 */

export const validateMobilePurchaseWithBackend = async (
  purchaseData: PurchaseData,
): Promise<MobileValidationResponse> => {
  try {
    // Basic validation
    if (!purchaseData.platform || !purchaseData.product_id) {
      return { success: false, message: 'Missing required purchase data' };
    }

    // Platform-specific validation
    if (purchaseData.platform === 'ios') {
      if (!purchaseData.receipt_data) {
        return {
          success: false,
          message: 'iOS purchases require receipt data',
        };
      }
    } else if (purchaseData.platform === 'android') {
      if (!purchaseData.purchase_token) {
        return {
          success: false,
          message: 'Android purchases require purchase token',
        };
      }
    }

    const payload = {
      platform: purchaseData.platform,
      product_id: purchaseData.product_id,
      ...(purchaseData.purchase_token && {
        purchase_token: purchaseData.purchase_token,
      }),
      ...(purchaseData.receipt_data && {
        receipt_data: purchaseData.receipt_data,
      }),
    };

    console.log('Sending validation request:', {
      platform: payload.platform,
      product_id: payload.product_id,
      has_purchase_token: !!payload.purchase_token,
      has_receipt_data: !!payload.receipt_data,
      receipt_data_length: payload.receipt_data?.length,
    });

    const response = await api.post('/subscriptions/validate', payload);
    const data = response.data as MobileValidationResponse & {
      subscription?: Subscription;
    };

    if (data.success) {
      return {
        success: true,
        data: data.subscription,
        message: 'Subscription validated successfully',
      };
    }

    return {
      success: false,
      message: data.message || 'Validation failed',
    };
  } catch (error: unknown) {
    console.error('Mobile purchase validation error:', error);

    // Structurally type the axios-shaped fields we read off the error. Each
    // access guards against undefined / mistyped shapes.
    const axiosErr = error as
      | {
          response?: {
            status?: number;
            data?: { message?: string; errors?: Record<string, unknown> };
            headers?: unknown;
          };
        }
      | undefined;

    // Log detailed error information
    if (axiosErr?.response) {
      console.error('Backend error response:', {
        status: axiosErr.response.status,
        data: axiosErr.response.data,
        headers: axiosErr.response.headers,
      });
    }

    // Try to extract validation errors from Laravel response
    let errorMessage = 'Network error. Please try again.';
    if (axiosErr?.response?.data?.message) {
      errorMessage = axiosErr.response.data.message;
    }
    if (axiosErr?.response?.data?.errors) {
      const errors = axiosErr.response.data.errors;
      console.error('Validation errors:', errors);
      errorMessage = Object.values(errors).flat().join(', ');
    }

    return {
      success: false,
      message: handleError(error, errorMessage),
    };
  }
};
